import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Download, Plus } from "lucide-react";
import { pirsApi, downloadBlob, type PirListItem } from "@/api/pirs";
import { Badge, Button } from "@/design-system/components/ui";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { PageHeader } from "@/components/layout/PageHeader";
import { useAuth } from "@/hooks/useAuth";
import { useDateFormatter } from "@/utils/datetime";
import { pirKeys } from "./pirKeys";

/**
 * Payment System v3.0 PR 5 — PIR list (design spec §10.6).
 */
export function PirsListPage() {
  const { hasPermission } = useAuth();
  const canGenerate = hasPermission("PIR.Generate");
  const { dateTime } = useDateFormatter();

  const list = useQuery({
    queryKey: pirKeys.list,
    queryFn: () => pirsApi.list(),
  });

  const columns: Column<PirListItem>[] = [
    {
      key: "reportNumber",
      header: "PIR #",
      render: (r) => (
        <Link to={`/pirs/${r.id}`} className="font-semibold hover:text-gold-shadow">
          {r.reportNumber}
        </Link>
      ),
    },
    {
      key: "scope",
      header: "Scope",
      render: (r) => {
        if (r.loanId) return <Badge status="info">Loan</Badge>;
        if (r.poolId) return <Badge status="info">Pool</Badge>;
        return <Badge status="info">All Live</Badge>;
      },
    },
    {
      key: "range",
      header: "Date Range",
      render: (r) =>
        r.dateRangeStart || r.dateRangeEnd
          ? `${r.dateRangeStart ?? "…"} → ${r.dateRangeEnd ?? "…"}`
          : "—",
    },
    { key: "generatedBy", header: "Generated By", render: (r) => r.generatedByUserId },
    {
      key: "generatedAt",
      header: "Generated At",
      render: (r) => dateTime(r.generatedAt),
    },
    {
      key: "totals",
      header: "Total Disbursements / Amount",
      render: (r) =>
        `${r.totalDisbursements} • $${r.totalAmount.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
    },
    {
      key: "actions",
      header: "",
      render: (r) => (
        <div className="flex items-center gap-space-2 justify-end">
          <button
            type="button"
            className="p-space-1 rounded-md text-gold-shadow hover:bg-white-warm"
            aria-label={`Download ${r.reportNumber} CSV`}
            onClick={async () => {
              const blob = await pirsApi.downloadCsv(r.id);
              downloadBlob(blob, `${r.reportNumber}.csv`);
            }}
          >
            <Download size={16} aria-hidden /> CSV
          </button>
          <button
            type="button"
            className="p-space-1 rounded-md text-gold-shadow hover:bg-white-warm"
            aria-label={`Download ${r.reportNumber} PDF`}
            onClick={async () => {
              const blob = await pirsApi.downloadPdf(r.id);
              downloadBlob(blob, `${r.reportNumber}.pdf`);
            }}
          >
            <Download size={16} aria-hidden /> PDF
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Payments"
        title="Payment Instruction Reports"
        description="Operator-facing reports listing every live disbursement awaiting an instruction to the bank. Generating a PIR seals the included disbursements."
        actions={
          canGenerate && (
            <Link to="/pirs/new">
              <Button>
                <Plus size={16} aria-hidden /> New PIR
              </Button>
            </Link>
          )
        }
      />
      <DataTable
        columns={columns}
        rows={list.data ?? []}
        loading={list.isLoading}
        error={list.isError ? (list.error as Error).message : null}
        emptyMessage="No PIRs have been generated yet."
        rowKey={(r) => r.id}
      />
    </>
  );
}
